import ChartHeader from '../ChartHeader';
import NoResponsesAvailableYet from '../NoResponsesAvailableYet';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { Box } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

const CompletionByGroupChart = ({ data = [], onRefresh, isFirstLoad }) => {
  const chartRef = useRef(null);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    if (onRefresh) {
      await onRefresh();
    }
    setTimeout(() => setIsManualRefreshing(false), 500);
  };

  useLayoutEffect(() => {
    if (!data || data?.length === 0) return;
    // Create root
    const root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Remove logo
    root._logo.dispose();

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout
      })
    );

    // Create axes - swap X and Y for horizontal bars
    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'groupName',
        renderer: am5xy.AxisRendererY.new(root, {
          minorGridEnabled: false,
          cellStartLocation: 0.1,
          cellEndLocation: 1
        }),
        tooltip: am5.Tooltip.new(root, {})
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 100,
        numberFormat: "#'%'",
        renderer: am5xy.AxisRendererX.new(root, {})
      })
    );

    // Hide all grid lines
    yAxis.get('renderer').grid.template.set('forceHidden', true);
    xAxis.get('renderer').grid.template.set('forceHidden', true);

    // Hide the X-axis completely
    xAxis.set('visible', false);

    // Optional: Also hide X-axis labels and ticks
    xAxis.get('renderer').labels.template.set('visible', false);
    xAxis.get('renderer').ticks.template.set('visible', false);

    // Increase font size of Y-axis labels with additional styling
    yAxis.get('renderer').labels.template.setAll({
      fontSize: 14,
      fontWeight: '500',
      fill: am5.color('#333333'),
      paddingRight: 20
    });

    // Add series for completion rate FIRST (this will appear from the left)
    const completedSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Participation',
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: 'completionRate',
        categoryYField: 'groupName',
        stacked: true,
        tooltip: am5.Tooltip.new(root, {
          labelText: '{categoryY}: {valueX}% ({completedCount}/{totalStudents})'
        })
      })
    );

    // Set completed series properties
    completedSeries.columns.template.setAll({
      tooltipText: '{categoryY}: {valueX}% ({completedCount}/{totalStudents})',
      height: am5.percent(70),
      strokeOpacity: 0,
      fill: am5.color('#336F9C'),
      cornerRadiusTL: 3,
      cornerRadiusBL: 3
    });

    // Add series for background (remaining portion) SECOND (this will stack after the blue)
    const remainingSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Remaining',
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: 'remainingRate',
        categoryYField: 'groupName',
        stacked: true
      })
    );

    // Set background series properties
    remainingSeries.columns.template.setAll({
      height: am5.percent(70),
      strokeOpacity: 0,
      fill: am5.color('#E0E0E0'),
      cornerRadiusTR: 3,
      cornerRadiusBR: 3
    });

    // Add data labels to the completed series
    completedSeries.bullets.push(() => {
      return am5.Bullet.new(root, {
        locationX: 0.5, // Position in the middle of the blue bar
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: '{valueX}%',
          fill: am5.color('#ffffff'),
          centerX: am5.p50,
          centerY: am5.p50,
          fontSize: 14,
          fontWeight: 'bold',
          populateText: true
        })
      });
    });

    const sortedData = data.sort((a, b) => {
      const numA = parseInt(a.groupName.match(/\d+/g)?.[0] || '0');
      const numB = parseInt(b.groupName.match(/\d+/g)?.[0] || '0');
      return numB - numA;
    });

    // Prepare chart data with both completion and remaining rates
    const chartData = sortedData?.map((item) => {
      const completionRate = Number(((item.completedCount / item.totalStudents) * 100).toFixed(1));
      const remainingRate = Number((100 - completionRate).toFixed(1));

      return {
        groupName: item.groupName,
        completionRate: completionRate,
        remainingRate: remainingRate,
        completedCount: item.completedCount,
        totalStudents: item.totalStudents
      };
    });

    // Set data for both series
    completedSeries.data.setAll(chartData);
    remainingSeries.data.setAll(chartData);
    yAxis.data.setAll(chartData);

    if (isFirstLoad) {
      // Make stuff animate on load
      completedSeries.appear(1000);
      remainingSeries.appear(1000);
      chart.appear(1000, 100);
    }

    return () => {
      root.dispose();
    };
  }, [data]);

  return (
    <Box>
      <ChartHeader
        title='Completion by group'
        description='This chart shows participation rates by year level.'
        showRefreshButton={data && data.length > 0}
        handleManualRefresh={handleManualRefresh}
        isRefreshing={isManualRefreshing}
      />
      {!data || data?.length === 0 ? (
        <Box sx={{ mt: 2 }}>
          <NoResponsesAvailableYet data={data} onRefresh={onRefresh} />
        </Box>
      ) : (
        <Box ref={chartRef} style={{ width: '100%', height: 'calc(100vh - 380px)' }} />
      )}
    </Box>
  );
};

export default CompletionByGroupChart;
